<template>
  <div class="SeminarYoutubePlayer">
    <div v-if="loading" class="SeminarYoutubePlayerStandby">
      <span
        role="img"
        aria-labelledby="読み込み中"
        class="c-icon SeminarYoutubePlayerStandby"
      >
        hourglass_empty
      </span>
    </div>
    <div v-else-if="standby" class="SeminarYoutubePlayerStandby">
      <!-- 開始前 -->
      <div class="SeminarYoutubePlayerStandby__inner">
        <p v-if="viewStartTime">
          <span class="d-inline-block">{{ viewStartTime }}から</span>
          <span class="d-inline-block">ライブ配信がスタートします</span>
        </p>
        <p v-else>ライブ配信を予定しています</p>
      </div>
    </div>
    <div v-else-if="!done" class="SeminarYoutubePlayerPlaying">
      <!-- 配信中 -->
      <YoutubeIframePlayer
        ref="player"
        :video-id="videoId"
        height="100%"
        width="100vw"
        autoplay
        :mute="isMute"
        :start="passedTimeSeconds"
        :end="duration"
        class="SeminarYoutubePlayerPlaying__player"
        @ready="onReady"
      />
      <div v-if="!disableSound" class="px-2 py-1">
        <button
          v-if="ready"
          type="button"
          class="btn btn-sm btn-dark"
          @click="toggleMute"
        >
          <span
            role="img"
            :aria-labelledby="isMute ? '消音' : '音を出す'"
            class="c-icon SeminarYoutubePlayerPlaying__icon"
          >
            {{ isMute ? 'volume_off' : 'volume_up' }}
          </span>
        </button>
      </div>
    </div>
    <div v-else class="SeminarYoutubePlayerDone">
      <!-- 配信完了 -->
      <div class="SeminarYoutubePlayerDone__inner">
        <p>本日のライブ配信は終了しました</p>
      </div>
    </div>
  </div>
</template>

<script>
import { dayjs, formatDateTimeShorten } from '@/helpers/datetime';
import YoutubeIframePlayer from '@/vue/components/YoutubeIframePlayer.vue';

/**
 * @typedef {string} Timeformat - HH:mm:ss(ex: 23:59:59)
 */
/**
 * @typedef {string} ISO8601Extended - ex: 2014-10-10T13:50:40+09:00
 */

/**
 * 曜日番号を返す
 *
 * @param {'monday'|'tuesday'|'wednesday'|'thursday'|'friday'|'saturday'|'sunday'}
 * @returns {number}
 */
function getWeekNumber(weekStr) {
  return [
    'sunday',
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
  ].findIndex((s) => s === weekStr);
}

const timeFormatLength = 'HH:mm:ss'.length;

export default {
  name: 'SeminarYoutubePlayer',

  components: {
    YoutubeIframePlayer,
  },

  props: {
    videoId: {
      type: String,
      default: '',
      required: true,
    },
    // セミナー開始日時
    start: {
      /** @type {Timeformat|ISO8601Extended|Timeformat[]|ISO8601Extended[]} */
      type: [String, Array],
      default: '',
    },
    // 動画の長さ・秒
    duration: {
      type: Number,
      default: undefined,
    },
    // 毎週
    weekly: {
      /** @type {('monday'|'tuesday'|'wednesday'|'thursday'|'friday'|'saturday'|'sunday')[]} */
      type: Array,
      default: undefined,
    },
    disableSound: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      /** @type {Date} 現在日時 */
      now: new Date(),
      /** @type {boolean} 開始まで待機状態か */
      standby: true,
      /** @type {boolean} 配信完了状態か */
      done: false,
      /** @type {number} N秒配信開始から経過 */
      passedTimeSeconds: 0,
      /** @type {number} 開始までN秒 */
      standbySeconds: -1,
      /** @type {number}  setTimeout ID */
      timerId: -1,
      /** @type {boolean} playerの準備ができたか */
      ready: false,
      /** @type {boolean} ミュートか */
      isMute: true,
      /** @type {boolean} loading */
      loading: false,
    };
  },

  computed: {
    /**
     * @returns {Dayjs}
     */
    nowDayJs() {
      return dayjs(this.now);
    },

    /**
     * startを各種設定に合わせた日付に変換する
     * 毎週設定(weekly)：今日の日付＋時間（startのHH:mm:ss）を返す
     * 複数時間設定(Timeformat[])：今日の日付＋直近の開始時間（Timeformat HH:mm:ss）を返す
     * 複数日時設定（ISO8601Extended[]）：直近の開始日時を返す
     *
     * @returns {ISO8601Extended}
     */
    formattedISOStringStart() {
      /** @type {Timeformat|ISO8601Extended} */
      const start = !Array.isArray(this.start)
        ? this.start
        : this.getISOStringLatestStart(this.start);

      if (!start) {
        return '';
      } else if (!this.weekly) {
        return start; // ISO8601 Extendedの想定
      }

      /** @type {ISO8601Extended} */
      let todayISOString = '';

      if (this.isTimeFormat(start)) {
        todayISOString = this.toISOStringToday(start);
      } else {
        // ISO8601 Extendedで設定されているとみなす
        const time = dayjs(start).format('HH:mm:ss');
        todayISOString = this.toISOStringToday(time);
      }

      return dayjs(todayISOString).isValid() ? todayISOString : '';
    },
    /**
     * @returns {string}
     */
    viewStartTime() {
      if (!this.formattedISOStringStart) return '';

      const format = this.weekly ? 'HH:mm' : 'YYYY年MM月DD日 HH:mm';
      return formatDateTimeShorten(
        this.now,
        this.formattedISOStringStart,
        format
      );
    },
    /**
     * weekly で指定した曜日を、曜日番号に変換した値
     * 0(日曜日)~6(土曜日)
     *
     * @returns {number[]}
     */
    weeklyWeekNumbers() {
      return Array.isArray(this.weekly)
        ? this.weekly.map((w) => getWeekNumber(w))
        : [];
    },
  },

  watch: {
    start() {
      this.setState().then(() => this.startReadyTimer());
    },
    duration() {
      this.setState();
    },
  },

  created() {
    this.loading = true;

    this.setState()
      .then(() => this.startReadyTimer())
      .finally(() => (this.loading = false));
  },

  methods: {
    /**
     * @returns {Promise<void>}
     */
    async setState() {
      this.now = new Date();

      if (!this.formattedISOStringStart) {
        this.standby = false;
        this.done = false;
        this.standbySeconds = -1;
        this.passedTimeSeconds = 0;
        return;
      }

      let diff = -1;

      if (this.weekly && !this.isTheWeeklyDay(this.nowDayJs)) {
        // 毎週設定：今日は開催日ではない
        this.standby = false;
        this.done = true;
      } else {
        // 毎週設定：今日が開催日 or 開催日指定
        const start = dayjs(this.formattedISOStringStart);
        diff = start.diff(this.nowDayJs, 's');

        this.standby = this.nowDayJs.isBefore(start);
        this.done = this.duration ? -diff >= this.duration : false;
      }

      if (this.standby) {
        // 開始待ち
        this.standbySeconds = diff;
        this.passedTimeSeconds = 0;
      } else if (!this.done) {
        // 配信中
        this.standbySeconds = -1;
        this.passedTimeSeconds = -diff;
      } else {
        // 配信終了
        this.standbySeconds = -1;
        this.passedTimeSeconds = 0;
      }
    },

    /**
     * 複数時間設定(Timeformat[])：今日の日付＋直近の開始時間を返す
     * 複数日時設定（ISO8601Extended[]）：直近の開始日時を返す
     * => なければ、最後の配信開始日時を返す
     *
     * @param {Timeformat[]|ISO8601Extended[]} starts
     * @returns {ISO8601Extended|null}
     */
    getISOStringLatestStart(starts) {
      if (!Array.isArray(starts)) return starts;

      /** @type {Dayjs[]} */
      const dayjses = starts.reduce((acc, start) => {
        if (!start) return acc;

        const dayjsObj = this.isTimeFormat(start)
          ? dayjs(this.toISOStringToday(start))
          : dayjs(start);

        if (dayjsObj.isValid()) {
          acc.push(dayjsObj);
        }
        return acc;
      }, []);

      if (!dayjses.length) return null;

      // 昇順ソート
      dayjses.sort((a, b) => (dayjs(a).isAfter(dayjs(b)) ? 1 : -1));

      /** @type {Dayjs|undefined} */
      const standbyOrPlaying = dayjses.find((startDayjs) => {
        const end = startDayjs.add(this.duration, 's');
        return !this.nowDayJs.isAfter(end);
      });

      return standbyOrPlaying
        ? standbyOrPlaying.toISOString()
        : dayjses[dayjses.length - 1]?.toISOString();
    },

    /**
     * 文字数だけのチェックだが
     * HH:mm:ss で設定されているとみなす
     *
     * @param {string} format
     * @returns {boolean}
     */
    isTimeFormat(format) {
      return format.length === timeFormatLength;
    },

    /**
     * @param {Timeformat} time
     * @returns {ISO8601|ISO8601Extended}
     */
    toISOStringToday(time) {
      if (this.isTimeFormat(time)) {
        return this.nowDayJs.format('YYYY-MM-DDT') + time + '+09:00';
      } else {
        return this.nowDayJs.toISOString();
      }
    },

    /**
     * 毎週設定があり、今日（now）が開催日か
     *
     * @param {Dayjs} nowDayjs
     * @returns {boolan}
     */
    isTheWeeklyDay(nowDayjs) {
      return this.weekly && this.weeklyWeekNumbers.includes(nowDayjs.day());
    },

    /**
     * スタンバイ状態で、開催日時が今日ならtimerを使う
     *
     * @returns {boolean}
     */
    useReadyTimer() {
      if (!this.standby) return false;

      const dayOfSeconds = 86400; // 60(s) * 60(m) * 24(h)
      return this.standbySeconds > -1 && this.standbySeconds < dayOfSeconds;
    },

    /**
     * @returns {void}
     */
    startReadyTimer() {
      if (!this.useReadyTimer()) return;

      // 秒 => ミリ秒
      const standbyMs = this.standbySeconds * 1000;

      this.resetReadyTimer();

      this.timerId = setTimeout(() => {
        this.standby = false;
        this.standbySeconds = -1;
        this.resetReadyTimer();
      }, standbyMs);
    },
    /**
     * @returns {void}
     */
    resetReadyTimer() {
      clearTimeout(this.timerId);
      this.timerId = -1;
    },

    /**
     * @returns {void}
     */
    onReady() {
      this.ready = true;
    },
    /**
     * @returns {void}
     */
    toggleMute() {
      if (!this.$refs.player) return;

      if (this.isMute) {
        this.$refs.player.unMuteVideo();
        this.isMute = false;
      } else {
        this.$refs.player.muteVideo();
        this.isMute = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@scss/01-settings/index";

.SeminarYoutubePlayerStandby,
.SeminarYoutubePlayerDone {
  background-color: #000;
  color: #fff;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  font-size: calc(100vw / 16);
  margin-bottom: 0;
  min-height: 100vh;
  text-align: center;
  font-weight: $font-weight-middle;
  line-height: $line-height-md;

  &__inner {
    padding-left: $container-padding-x;
    padding-right: $container-padding-x;
  }
}

.SeminarYoutubePlayerPlaying {
  display: flex;
  flex-direction: column;
  flex-wrap: nowrap;
  max-height: 100vh;
  height: 100vh;

  &__player {
    flex: 1 auto;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.15);
  }
  &__icon {
    vertical-align: bottom;
    font-size: 1.5em;
    top: 0.08em;
  }
}
</style>
